import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Trash } from "@phosphor-icons/react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  buildDimensionFormDefaults,
  flattenDimensionItems,
  renderDimensionFormCells,
} from "../Categories/components/Category/helpers";
import { useDimensionFiltering } from "../Categories/components/Category/useDimensionFiltering";
import { isFactorRequiredCategory, PURCHASED_CATEGORIES, RENEWABLE_TYPES } from "./const";
import { LIME_FOCUS, mapPurchasedFormDataToApiData, resolveKwhMeasureLabel } from "./helpers";
import { DECIMAL_STEP, exceedsMaxDecimals, MAX_DECIMAL_PLACES } from "../../../../utils/decimals";
import {
  renderDimensionHeaderCells,
  activeDimensionCount,
} from "./dimensionDisplay";
import CategoryInfoPopover from "./CategoryInfoPopover";

const FIELD_ARRAY_NAME = "purchasedElectricity";

const TH_PROPS = {
  py: 3,
  px: 4,
  fontSize: "13px",
  color: "gray.600",
  fontWeight: "600",
  textTransform: "none",
  letterSpacing: "normal",
};

// Emission factor is only editable for the three factor-bearing categories.
const isFactorEditable = (category) =>
  isFactorRequiredCategory(category) || category === "CERTIFIED_RENEWABLE";

/**
 * RHF modal for adding (and editing a single row of) purchased electricity.
 * Mirrors BusinessTravelModalWrapper's useForm + useFieldArray + dimension
 * filtering wiring, but renders the mock's own table layout. Per-row
 * conditional logic:
 *  - emission factor editable for SUPPLIER_CONTRACTED / DIRECT_PRIVATE_WIRE
 *    (required) and CERTIFIED_RENEWABLE (defaults to "0").
 *  - renewable Type select shown only for CERTIFIED_RENEWABLE (required).
 *  - supplier note shown only for factor-required categories.
 */
function PurchasedElectricityModal({
  isOpen,
  onClose,
  onSubmitPurchased,
  organizationStructure,
  dimensionItems,
  editData,
  kwhMeasureId,
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope2V2",
  });

  const isEdit = !!editData;
  const measureLabel = resolveKwhMeasureLabel(organizationStructure);
  const dimCount = activeDimensionCount(organizationStructure);

  const buildEmptyRow = () => ({
    ...buildDimensionFormDefaults(organizationStructure, dimensionItems),
    category: "",
    value: "",
    emissionFactor: "",
    renewableType: "",
    supplierNote: "",
  });

  // Seed an edit row from the API record: flatten its dimensionItems and lift
  // the v2 fields into the flat form shape.
  const seededEditData = editData
    ? {
        ...flattenDimensionItems({ ...editData }),
        value: editData.consumption?.value ?? "",
        emissionFactor: editData.emissionFactor ?? "",
        renewableType: editData.category === "CERTIFIED_RENEWABLE" ? editData.certificateType ?? "" : "",
        supplierNote: editData.supplierNote ?? "",
      }
    : null;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      [FIELD_ARRAY_NAME]: [seededEditData ? seededEditData : buildEmptyRow()],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: FIELD_ARRAY_NAME });
  const watchFieldArray = watch(FIELD_ARRAY_NAME);

  const { getFilteredItems, setChangedRowIndex } = useDimensionFiltering({
    dimensionItems,
    editData: seededEditData,
    watchFieldArray,
  });

  // Track whether the user attempted to save so per-row "Required" hints only
  // show after a blocked submit (matches the mock's inline-error feel).
  const [showErrors, setShowErrors] = useState(false);

  // When the category changes, derive a sensible emission factor and wipe
  // fields that no longer apply — same rules as the mock's updateDraft.
  const handleCategoryChange = (index, value) => {
    const current = watchFieldArray[index] || {};
    let nextFactor = current.emissionFactor;
    if (value === "STANDARD_GRID") nextFactor = "";
    else if (value === "CERTIFIED_RENEWABLE") nextFactor = "0";
    setValue(`${FIELD_ARRAY_NAME}.${index}.emissionFactor`, nextFactor);
    if (value !== "CERTIFIED_RENEWABLE") {
      setValue(`${FIELD_ARRAY_NAME}.${index}.renewableType`, "");
    }
    if (!isFactorRequiredCategory(value)) {
      setValue(`${FIELD_ARRAY_NAME}.${index}.supplierNote`, "");
    }
  };

  const isRowValid = (row) =>
    !!row.category &&
    row.value !== "" &&
    row.value !== null &&
    row.value !== undefined &&
    (!isFactorRequiredCategory(row.category) ||
      (row.emissionFactor !== "" && row.emissionFactor !== null && row.emissionFactor !== undefined)) &&
    (row.category !== "CERTIFIED_RENEWABLE" || !!row.renewableType) &&
    !exceedsMaxDecimals(row.value);

  const submit = (formValues) => {
    const rows = formValues[FIELD_ARRAY_NAME];
    // Block save when any row has a factor-required category with an empty
    // factor, or a Certified Renewable row missing its renewable type.
    const hasBlockingRow = rows.some((row) => !isRowValid(row));
    if (hasBlockingRow) {
      setShowErrors(true);
      return;
    }
    onSubmitPurchased(mapPurchasedFormDataToApiData(rows, kwhMeasureId), isEdit);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        borderRadius="lg"
        w="fit-content"
        maxW="calc(100vw - 32px)"
        minW={{ base: "auto", md: "640px" }}
      >
        <ModalHeader fontSize="20px" fontWeight="700" color="darkTeal">
          <Flex align="center" gap={2}>
            <Text>{t("addPurchased")}</Text>
            <CategoryInfoPopover />
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflowX="auto">
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr bg="gray.50">
                  {renderDimensionHeaderCells(organizationStructure)}
                  <Th {...TH_PROPS}>{t("columns.category")}</Th>
                  <Th {...TH_PROPS}>{t("columns.consumptionValue")}</Th>
                  <Th {...TH_PROPS}>{t("columns.measure")}</Th>
                  <Th {...TH_PROPS}>{t("columns.emissionFactor")}</Th>
                  <Th {...TH_PROPS}>{t("columns.type")}</Th>
                  <Th {...TH_PROPS}>{t("columns.supplierNote")}</Th>
                  <Th py={3} px={4} width="48px" />
                </Tr>
              </Thead>
              <Tbody>
                {fields.map((field, index) => {
                  const row = watchFieldArray[index] || {};
                  const category = row.category;
                  const factorEditable = isFactorEditable(category);
                  const factorRequired = isFactorRequiredCategory(category);
                  const factorMissing =
                    showErrors &&
                    factorRequired &&
                    (row.emissionFactor === "" || row.emissionFactor === null || row.emissionFactor === undefined);
                  const renewableMissing =
                    showErrors && category === "CERTIFIED_RENEWABLE" && !row.renewableType;
                  const valueTooPrecise = showErrors && exceedsMaxDecimals(row.value);

                  // Dimension cells (FormSelect, lime focus built in).
                  const dimCells = renderDimensionFormCells({
                    organizationStructure,
                    dimensionItems,
                    register,
                    fieldArrayName: FIELD_ARRAY_NAME,
                    index,
                    watchFieldArray,
                    getFilteredItems,
                    onDimensionChange: setChangedRowIndex,
                  });

                  const categorySelector = register(`${FIELD_ARRAY_NAME}.${index}.category`);

                  return (
                    <Tr key={field.id} borderTop={index === 0 ? "none" : "1px solid"} borderColor="gray.100">
                      {dimCells.map((cell, dimIdx) => (
                        <Td key={`dim-${field.id}-${dimIdx}`} py={3} px={4}>
                          {cell}
                        </Td>
                      ))}
                      <Td py={3} px={4}>
                        <Select
                          size="sm"
                          borderRadius="md"
                          placeholder=" "
                          {...LIME_FOCUS}
                          {...categorySelector}
                          onChange={(e) => {
                            categorySelector.onChange(e);
                            handleCategoryChange(index, e.target.value);
                          }}
                        >
                          {PURCHASED_CATEGORIES.map((code) => (
                            <option key={code} value={code}>{t(`categories.${code}`)}</option>
                          ))}
                        </Select>
                      </Td>
                      <Td py={3} px={4}>
                        <Input
                          size="sm"
                          type="number"
                          step={DECIMAL_STEP}
                          borderRadius="md"
                          onWheel={(e) => e.target.blur()}
                          borderColor={
                            valueTooPrecise ||
                            (showErrors && (row.value === "" || row.value === null || row.value === undefined))
                              ? "red.400"
                              : undefined
                          }
                          {...LIME_FOCUS}
                          {...register(`${FIELD_ARRAY_NAME}.${index}.value`)}
                        />
                        {valueTooPrecise && (
                          <Text fontSize="11px" color="red.500" mt={1}>
                            {t("maxDecimals", { count: MAX_DECIMAL_PLACES })}
                          </Text>
                        )}
                      </Td>
                      <Td py={3} px={4} fontSize="14px" color="gray.700">{measureLabel}</Td>
                      <Td py={3} px={4}>
                        {factorEditable ? (
                          <Box>
                            <Input
                              size="sm"
                              type="number"
                              step={DECIMAL_STEP}
                              borderRadius="md"
                              placeholder="e.g. 0.205"
                              onWheel={(e) => e.target.blur()}
                              onKeyDown={(e) => {
                                if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                              }}
                              borderColor={factorMissing ? "red.400" : undefined}
                              {...LIME_FOCUS}
                              {...register(`${FIELD_ARRAY_NAME}.${index}.emissionFactor`)}
                            />
                            {factorMissing && (
                              <Text fontSize="11px" color="red.500" mt={1}>{t("required")}</Text>
                            )}
                          </Box>
                        ) : (
                          <Text fontSize="14px" color="gray.400">—</Text>
                        )}
                      </Td>
                      <Td py={3} px={4}>
                        {category === "CERTIFIED_RENEWABLE" ? (
                          <Box>
                            <Select
                              size="sm"
                              borderRadius="md"
                              placeholder={t("columns.type")}
                              borderColor={renewableMissing ? "red.400" : undefined}
                              {...LIME_FOCUS}
                              {...register(`${FIELD_ARRAY_NAME}.${index}.renewableType`)}
                            >
                              {RENEWABLE_TYPES.map((code) => (
                                <option key={code} value={code}>{t(`renewableTypes.${code}`)}</option>
                              ))}
                            </Select>
                            {renewableMissing && (
                              <Text fontSize="11px" color="red.500" mt={1}>{t("required")}</Text>
                            )}
                          </Box>
                        ) : (
                          <Text fontSize="14px" color="gray.400">—</Text>
                        )}
                      </Td>
                      <Td py={3} px={4}>
                        {factorRequired ? (
                          <Textarea
                            size="sm"
                            borderRadius="md"
                            rows={1}
                            resize="none"
                            placeholder="e.g. Private wire from on-site CHP plant"
                            {...LIME_FOCUS}
                            {...register(`${FIELD_ARRAY_NAME}.${index}.supplierNote`)}
                          />
                        ) : (
                          <Text fontSize="14px" color="gray.400">—</Text>
                        )}
                      </Td>
                      <Td py={3} px={4} textAlign="right">
                        <IconButton
                          aria-label="Remove draft row"
                          icon={<Trash size={14} weight="bold" />}
                          size="sm"
                          variant="ghost"
                          color="gray.500"
                          isDisabled={fields.length <= 1}
                          _hover={{ color: "red.500", bg: "red.50" }}
                          onClick={() => remove(index)}
                        />
                      </Td>
                    </Tr>
                  );
                })}
                {!isEdit && (
                  <Tr bg="lightLime">
                    <Td colSpan={dimCount + 7} py={3} px={4}>
                      <Text
                        as="button"
                        type="button"
                        fontSize="14px"
                        fontWeight="600"
                        color="limeShade.700"
                        onClick={() => append(buildEmptyRow())}
                      >
                        {t("addAnother")}
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button
            bg="lime"
            color="pureBlack"
            borderRadius="full"
            _hover={{ bg: "limeShade.300" }}
            onClick={handleSubmit(submit)}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default PurchasedElectricityModal;
